import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { IOrderType } from 'src/app/appInterfaces/IOrderType';
import { IStore } from 'src/app/appInterfaces/IStore';
import { LoaderUtility } from 'src/app/appUtilities/loader.utility';
import { StoresService } from './stores.service';

@Injectable({
  providedIn: 'root'
})
export class StoreResolver {

  private readonly selectedStoreStorageKey = 'store';
  changeStoreClick = new Subject<void>();
  storeList = new BehaviorSubject<IStore[]>(null);
  selectedStore = new BehaviorSubject<IStore>(null);
  selectedOrderType = new BehaviorSubject<IOrderType>(null);
  orderTypeList = new BehaviorSubject<IOrderType[]>(null);
  private orderTypeStoreId: number = null;

  constructor(
    private _storeService: StoresService,
    private _loaderUtility: LoaderUtility
  ) {
    const storedStore = this.getStoredSelectedStore();
    if (storedStore) {
      this.selectedStore.next(storedStore);
    }
  }



  /**
   * Prefer active stores, but fall back to every returned store when none are active.
   * This matches TastioV2 store selection, which still lets a tenant choose from the
   * store API result instead of blocking on an empty active-store subset.
   */
  operationalStoresFrom(storeList: IStore[] | null): IStore[] {
    if (!storeList?.length) {
      return [];
    }
    const validStores = storeList.filter((store) => store?.id != null);
    const activeStores = validStores.filter((store) => store.active !== false);
    return activeStores.length ? activeStores : validStores;
  }

  /**
   * If exactly one operational store exists, select it (caller should invoke getOrderTypeList when appropriate).
   */
  applyDefaultStoreIfSingleOperational(storeList: IStore[] | null): void {
    const operational = this.operationalStoresFrom(storeList);
    if (operational.length !== 1) {
      return;
    }
    const only = operational[0];
    if (this.selectedStore.value?.id === only.id) {
      return;
    }
    this.setSelectedStore(only);
  }

  /**
   * subscribe store list
   */
   subscribeStoreList(force = false) {
    if(!force && this.storeList.value !== null) {
      this.getOrderTypeList();
      return
    }
    this._storeService.getAllStores().subscribe((res: IStore[]) => {
        this.setStoreList(res);
        this.getOrderTypeList();
    });
  }


  
  /**
   * Set Store list
   * @param storeList 
   */
   setStoreList(storeList: IStore[]) {
    this.storeList.next(storeList);
    this.restoreSelectedStoreFromList(storeList);
  }

  

  /**
   * set selected store
   * @param store 
   */
  public setSelectedStore(store: IStore) {
    if (!store) {
      this.clearSelectedStore();
      return;
    }
    if (this.selectedStore.value?.id !== store.id) {
      this.orderTypeStoreId = null;
      this.orderTypeList.next(null);
      this.selectedOrderType.next(null);
    }
    localStorage.setItem(this.selectedStoreStorageKey, JSON.stringify(store));
    this.selectedStore.next(store);
  }


  /**
   * clear Selected store
   */
  public clearSelectedStore(): void {
    localStorage.removeItem(this.selectedStoreStorageKey);
    this.selectedStore.next(null);
  }

  public getOrderTypeList() {
    const storeId = this.selectedStore.value?.id;
    if(!storeId) {
      return;
    }
    if (this.orderTypeStoreId === storeId && this.orderTypeList.value) {
      return;
    }
    this._storeService.getAllOrderTypes(storeId).subscribe(res => {
      if (res) {
        this.orderTypeStoreId = storeId;
        this.orderTypeList.next(res);
        this.setSelectedOrderType(this.orderTypeList.value[0]);
      }
    });
  }

  setSelectedOrderType(orderType: IOrderType) {
    this.selectedOrderType.next(orderType);
  }

/**
 * Clear storage
 */
  clear() {
    this.storeList.next(null);
    this.orderTypeList.next(null);
    this.selectedOrderType.next(null);
    this.orderTypeStoreId = null;
    this.clearSelectedStore();
  }

  private restoreSelectedStoreFromList(storeList: IStore[] | null): void {
    if (!storeList?.length) {
      return;
    }

    const selectedStore = this.selectedStore.value || this.getStoredSelectedStore();
    if (selectedStore?.id == null) {
      return;
    }

    const matchingStore = this.operationalStoresFrom(storeList).find((store) => store.id === selectedStore.id);
    if (matchingStore) {
      if (this.selectedStore.value !== matchingStore) {
        this.setSelectedStore(matchingStore);
      }
      return;
    }

    this.clearSelectedStore();
  }

  private getStoredSelectedStore(): IStore | null {
    const storedStore = localStorage.getItem(this.selectedStoreStorageKey);
    if (!storedStore) {
      return null;
    }

    try {
      const parsedStore = JSON.parse(storedStore) as IStore;
      return parsedStore?.id == null ? null : parsedStore;
    } catch {
      localStorage.removeItem(this.selectedStoreStorageKey);
      return null;
    }
  }

  /**
   * Setup subscribe store list
   */
  async setupStore(set?:boolean) {
    if(this.storeList.value) {
      return
    }
    
    await this._loaderUtility.showLoader();
    this._storeService.getAllStores().subscribe(
      async (res: IStore[]) => {
        await this._loaderUtility.hideLoader();
        this.setStoreList(res);
        if(set) this.changeStoreClick.next();
      },
      async (err)=>{
        await this._loaderUtility.hideLoader();
      }
    );
  }
}
